<script>
  import {
    add, evolve, clamp, identity,
} from 'ramda';
  import { onMount } from 'svelte';
  import { derived } from 'svelte/store';

  export let tooltip;

  const visible = derived(tooltip.visible, identity);

  // visibile is used to trigger recomputation of the transform, but not used
  // in the actual calculation.
  const anchorTransform = derived([tooltip.anchor, visible], ([$anchor, isVisible]) => {
    if (!$anchor || !isVisible) {
      return undefined;
    }

    const relativeToDocument = evolve({
      top: add(window.scrollY),
      bottom: add(window.scrollY),
      left: add(window.scrollX),
      right: add(window.scrollX),
    });

    const position = relativeToDocument($anchor.getBoundingClientRect());

    const SCREEN_EDGE_OFFSET = 24;
    const tooltipXBounds = [
      tooltip.element.offsetWidth / 2 + SCREEN_EDGE_OFFSET,
      window.innerWidth - (tooltip.element.offsetWidth / 2 + SCREEN_EDGE_OFFSET),
    ];

    const center = {
      x: clamp(...tooltipXBounds, position.left + position.width / 2),
      y: position.top + position.height / 2,
    };

    const margin = 4;

    const transforms = {
      left: `translate(${position.left - margin * 2}px, ${center.y - margin}px) translate(-100%, -50%)`,
      right: `translate(${position.right}px, ${center.y - margin}px) translateY(-50%)`,
      bottom: `translate(${center.x - margin}px, ${position.bottom}px) translateX(-50%)`,
      top: `translate(${center.x - margin}px, ${position.top - margin * 2}px) translate(-50%, -100%)`,
    };

    return transforms[tooltip.gravity] ?? transforms.top;
  });

  let container;
  onMount(() => {
    container.append(tooltip.element);
    return () => tooltip.element.remove();
  });
</script>

<div
  class:show={$visible}
  style:transform={$anchorTransform}
  bind:this={container}
  class='tooltip-container'
  />

<style lang="postcss">
  .tooltip-container {
    position: absolute;
    top: 0;
    left: 0;
    margin: var(--spacing--xxs);
    padding: var(--spacing--xxs) var(--spacing--xs);
    border-radius: var(--radius--xs);
    max-width: 180px;
    background-color: var(--color--standout--background);
    color: var(--color--standout--text);
    font: var(--font--outfit-11);
    opacity: 0;
    pointer-events: none;
    z-index: var(--Tooltip--z-index);
    transition: opacity var(--duration--extra-fast) var(--ease--on-screen);

    &:hover,
    &.show {
      opacity: 1;
      pointer-events: default;
    }
  }

  @media (--screen-width--above-phone) {
    .tooltip-container {
      max-width: 404px;
    }
  }
</style>
